/**
 * M10 — Sección Formación (BIB-02 parte 1).
 *
 * - Identificación ligera por persona (C4): selector del equipo; el staff_id
 *   activo vive en localStorage y viaja en cada llamada de progreso.
 * - Player propio (C5): <video> con URL firmada temporal y heartbeat de
 *   progreso; la URL se re-firma sola si expira a mitad de reproducción.
 *   Los PDF se abren embebidos (iframe + URL de play inline) y al abrirlos
 *   se registra la lectura como «visto».
 *
 * Módulo autónomo: solo se engancha vía custom_functions['load_formacion'].
 */
import { page_text } from '../farmaval.js'
import {
    getTrainingLibrary,
    getTrainingStaff,
    getTrainingPlayUrl,
    getTrainingDownloadUrl,
    postTrainingProgress,
    getTrainingSummary,
    getTrainingQuiz,
    getTrainingAwards,
    getTrainingAwardDownload,
    postMentoringRequest,
} from '../clients/farmavel/index.js'
import { openQuiz, closeQuiz } from './quiz.js'
import { takePendingWorkshop } from './workshops.js'
import { getTrainingWorkshop } from '../clients/farmavel/index.js'

const STORAGE_ID = 'farmaval_training_staff_id'
const STORAGE_NAME = 'farmaval_training_staff_name'
const HEARTBEAT_SECONDS = 15

const state = {
    tree: null, // { areas: [], tree: { area: [nodes] } }
    path: [], // pila de carpetas abiertas
    completed: new Set(), // file_id vistos por la persona activa
    player: { file: null, timer: null, lastTick: 0, ended: false },
}

const t = (key) => page_text?.[key] ?? key

const el = (id) => document.getElementById(id)

const areaLabels = { respiratory: 'Respiratorio', probiomentor: 'ProBioMentor' }

const kindLabels = () => ({
    pill_video: t('formacion_kind_video'),
    long_video: t('formacion_kind_video'),
    pdf: 'PDF',
    link: t('formacion_kind_link'),
    template: t('formacion_kind_template'),
    image: t('formacion_kind_image'),
})

function isMirror() {
    return localStorage.getItem('farmaval_mirror') === '1'
}

export function getActiveStaff() {
    // BIB-05: en la farmacia espejo la identidad es el propio mentor — sin
    // selector de equipo ni staff_id (el backend resuelve por su token).
    if (isMirror()) {
        return { id: null, name: localStorage.getItem('farmaval_mirror_name') ?? 'Mentor' }
    }

    const id = localStorage.getItem(STORAGE_ID)
    return id ? { id: Number(id), name: localStorage.getItem(STORAGE_NAME) ?? '' } : null
}

export function resetFormacionSession() {
    localStorage.removeItem(STORAGE_ID)
    localStorage.removeItem(STORAGE_NAME)
    localStorage.removeItem('farmaval_mirror')
    localStorage.removeItem('farmaval_mirror_name')
    document.body.classList.remove('mirror-mode')
    state.tree = null
    state.path = []
    state.completed = new Set()
    closePlayer()
}

/** Punto de entrada: registrado en custom_functions como `load_formacion`. */
export async function load_formacion() {
    bindStaticHandlers()
    const staff = getActiveStaff()

    if (!staff) {
        await showSelector()
        return
    }

    showHeader(staff)
    await loadLibrary()
}

let handlersBound = false
function bindStaticHandlers() {
    if (handlersBound) return
    handlersBound = true

    el('formacion_change_person')?.addEventListener('click', async (e) => {
        e.preventDefault()
        resetFormacionSession()
        await showSelector()
    })

    el('formacion_player_close')?.addEventListener('click', (e) => {
        e.preventDefault()
        closePlayer()
    })
}

// ── Selector de persona (C4) ───────────────────────────────────────────────

async function showSelector() {
    el('formacion_body').classList.add('hide')
    el('formacion_person').classList.add('hide')
    el('formacion_selector').classList.remove('hide')

    const list = el('formacion_staff_list')
    list.innerHTML = ''
    el('formacion_no_staff').classList.add('hide')

    const response = await getTrainingStaff()
    const staff = response?.data ?? []

    if (!staff.length) {
        el('formacion_no_staff').classList.remove('hide')
        return
    }

    staff.forEach((member) => {
        const item = document.createElement('li')
        item.textContent = member.staff_name
        item.addEventListener('click', async () => {
            localStorage.setItem(STORAGE_ID, String(member.staff_id))
            localStorage.setItem(STORAGE_NAME, member.staff_name)
            showHeader({ id: member.staff_id, name: member.staff_name })
            await loadLibrary()
        })
        list.appendChild(item)
    })
}

function showHeader(staff) {
    el('formacion_selector').classList.add('hide')
    el('formacion_person').classList.remove('hide')
    el('formacion_person_name').textContent = staff.name
    el('formacion_change_person').classList.toggle('hide', isMirror())
    el('formacion_body').classList.remove('hide')
}

// ── Biblioteca ─────────────────────────────────────────────────────────────

async function loadLibrary() {
    const staff = getActiveStaff()
    const [library, summary] = await Promise.all([
        getTrainingLibrary(),
        getTrainingSummary(staff.id),
    ])

    state.tree = library?.data ?? { areas: [], tree: {} }
    state.completed = new Set(
        (summary?.data?.progress ?? [])
            .filter((row) => row.completed)
            .map((row) => row.file_id)
    )
    state.path = []

    // TL-01: si se llegó desde el icono de un taller, abrir su formación.
    const workshopCode = takePendingWorkshop()
    if (workshopCode) {
        await openWorkshop(workshopCode)
        return
    }

    render()
}

/** TL-01: la formación del taller como raíz propia (fuera de la biblioteca general). */
async function openWorkshop(serviceCode) {
    const response = await getTrainingWorkshop(serviceCode)
    const folders = response?.data?.folders ?? []

    if (!folders.length) {
        render()
        return
    }

    state.workshopService = serviceCode

    const root = folders.length === 1
        ? { ...folders[0], type: 'workshop' }
        : {
              id: `workshop-${serviceCode}`,
              name: t(`${serviceCode.toLowerCase()}_name`),
              type: 'workshop',
              pb_block: null,
              locked: false,
              files: [],
              children: folders,
          }

    state.path = [root]
    render()
}

function currentNodes() {
    if (!state.path.length) return null
    return state.path[state.path.length - 1]
}

function render() {
    closePlayer()
    closeQuizIfOpen()
    const grid = el('formacion_grid')
    grid.innerHTML = ''
    el('formacion_empty').classList.add('hide')
    renderBreadcrumb()

    const current = currentNodes()

    if (!current) {
        renderRoot(grid)
        loadProgramResult()
        return
    }

    el('formacion_result')?.classList.add('hide')

    current.children.forEach((child) => grid.appendChild(folderCard(child)))
    current.files.forEach((file) => grid.appendChild(fileCard(file)))

    if (!current.children.length && !current.files.length) {
        el('formacion_empty').classList.remove('hide')
    }

    loadQuizButton(current)
    addMentoringButton(current)
}

/**
 * BIB-07 (C8): al final de cada bloque formativo, el EQUIPO puede pedir
 * agendar la mentoría con su mentor. Es intención de agendar — no exige que
 * todo el mundo haya terminado (C7) y no lo dispara ver contenido.
 * En un taller va solo en su portada: sus carpetas son solo contenido.
 */
function addMentoringButton(folderNode) {
    if (isMirror()) return
    if (folderNode.type !== 'pb_block' && folderNode.type !== 'workshop') return
    if (folderNode.type === 'workshop' && folderNode !== state.path[0]) return

    const grid = el('formacion_grid')
    const button = document.createElement('a')
    button.className = 'formacion_mentoring_btn'
    button.textContent = t('formacion_mentoring_btn')
    button.addEventListener('click', async (e) => {
        e.preventDefault()
        if (!confirm(t('formacion_mentoring_confirm'))) return

        const staff = getActiveStaff()
        const isWorkshop = folderNode.type === 'workshop'
        const payload = isWorkshop
            ? { origin: 'workshop', service: state.workshopService }
            : { origin: 'pb_block', block: folderNode.pb_block, folder_id: folderNode.id }
        if (!isWorkshop && typeof folderNode.id === 'number') payload.folder_id = folderNode.id
        if (staff && staff.id !== null) payload.staff_id = staff.id

        const response = await postMentoringRequest(payload)
        if (response?.data) {
            alert(response.data.already_pending
                ? t('formacion_mentoring_already')
                : t('formacion_mentoring_sent'))
        }
    })
    grid.appendChild(button)
}

/** BIB-02: si la carpeta tiene cuestionario activo, ofrece hacerlo. */
async function loadQuizButton(folderNode) {
    const staff = getActiveStaff()
    if (!staff) return

    const response = await getTrainingQuiz(folderNode.id, staff.id)
    const quiz = response?.data?.quiz

    // La navegación pudo cambiar mientras llegaba la respuesta.
    if (!quiz || currentNodes()?.id !== folderNode.id) return

    const grid = el('formacion_grid')
    const button = document.createElement('a')
    button.className = 'formacion_quiz_btn'
    button.textContent = quiz.best_score !== null
        ? `${t('formacion_quiz_btn')} (${t('formacion_quiz_best')}: ${quiz.best_score}%)`
        : t('formacion_quiz_btn')
    button.addEventListener('click', (e) => {
        e.preventDefault()
        openQuiz(quiz, staff.id, () => render())
    })
    grid.appendChild(button)
}

function renderRoot(grid) {
    const areas = state.tree.areas ?? []

    if (!areas.length) {
        el('formacion_empty').classList.remove('hide')
        return
    }

    areas.forEach((area) => {
        const nodes = state.tree.tree[area] ?? []
        if (!nodes.length) return

        if (areas.length > 1) {
            const title = document.createElement('h3')
            title.className = 'formacion_area_title'
            title.textContent = areaLabels[area] ?? area
            grid.appendChild(title)
        }

        nodes.forEach((node) => grid.appendChild(folderCard(node)))
    })
}

/** BIB-03: card «Mi resultado» del itinerario PB en la portada de Formación. */
async function loadProgramResult() {
    const staff = getActiveStaff()
    const box = el('formacion_result')
    if (!staff || !box || isMirror() || staff.id === null) {
        box?.classList.add('hide')
        return
    }

    const response = await getTrainingAwards(staff.id)
    const data = response?.data

    if (!data || !data.program || state.path.length) {
        box.classList.add('hide')
        return
    }

    el('formacion_result_avg').textContent = data.average !== null ? `${data.average}%` : '—'

    const blocks = el('formacion_result_blocks')
    blocks.innerHTML = ''
    data.blocks.forEach((block) => {
        const chip = document.createElement('span')
        const label = `B${block.number}: ${block.best !== null ? block.best + '%' : block.has_quiz ? t('formacion_result_pending') : t('formacion_result_noquiz')}`
        chip.textContent = label
        if (block.best !== null) chip.className = block.best >= data.pass_threshold ? 'ok' : 'low'
        blocks.appendChild(chip)
    })

    const awardBox = el('formacion_result_award')
    awardBox.innerHTML = ''

    if (data.award) {
        const link = document.createElement('a')
        link.textContent = data.award.kind === 'diploma'
            ? t('formacion_result_diploma')
            : t('formacion_result_certificate')
        link.addEventListener('click', async (e) => {
            e.preventDefault()
            const download = await getTrainingAwardDownload(staff.id)
            if (download?.data?.url) window.open(download.data.url, '_blank', 'noopener')
        })
        awardBox.appendChild(link)

        if (data.award.kind !== 'diploma') {
            const hint = document.createElement('p')
            hint.textContent = t('formacion_result_retake_hint')
            awardBox.appendChild(hint)
        }
    } else if (!data.complete) {
        const hint = document.createElement('p')
        hint.textContent = t('formacion_result_incomplete')
        awardBox.appendChild(hint)
    }

    box.classList.remove('hide')
}

function renderBreadcrumb() {
    const breadcrumb = el('formacion_breadcrumb')
    breadcrumb.innerHTML = ''

    const home = document.createElement('a')
    home.textContent = t('formacion_home')
    home.addEventListener('click', () => {
        state.path = []
        render()
    })
    breadcrumb.appendChild(home)

    state.path.forEach((node, index) => {
        const sep = document.createElement('span')
        sep.className = 'sep'
        sep.textContent = '›'
        breadcrumb.appendChild(sep)

        const link = document.createElement('a')
        link.textContent = node.name
        link.addEventListener('click', () => {
            state.path = state.path.slice(0, index + 1)
            render()
        })
        breadcrumb.appendChild(link)
    })
}

/**
 * Nombre de una tarjeta con un punto de corte tras cada «_»: los nombres de
 * archivo no llevan espacios y el navegador no tenía dónde partirlos, así que
 * se salían de la tarjeta. El nombre completo va en el title, porque el CSS
 * corta a 3 líneas.
 */
function cardName(text) {
    const value = String(text ?? '')
    const name = document.createElement('div')
    name.className = 'card_name'
    name.title = value

    value.split('_').forEach((part, index) => {
        if (index > 0) name.append('_', document.createElement('wbr'))
        name.append(part)
    })

    return name
}

function folderCard(node) {
    const card = document.createElement('div')
    card.className = 'formacion_card' + (node.locked ? ' locked' : '') + (node.cover_url ? ' has_cover' : '')

    if (node.cover_url) {
        const cover = document.createElement('img')
        cover.className = 'card_cover'
        cover.src = node.cover_url
        cover.alt = node.name
        cover.loading = 'lazy'
        cover.addEventListener('error', () => {
            cover.remove()
            card.classList.remove('has_cover')
        })
        card.appendChild(cover)
    }

    const kind = document.createElement('div')
    kind.className = 'card_kind'
    kind.textContent = node.type === 'pb_block'
        ? `${t('formacion_block')} ${node.pb_block ?? ''}`
        : t('formacion_folder')
    card.appendChild(kind)

    card.appendChild(cardName(node.name))

    if (node.locked) {
        const meta = document.createElement('div')
        meta.className = 'card_meta'
        meta.textContent = '🔒 ' + t('formacion_locked')
        card.appendChild(meta)
        return card
    }

    card.addEventListener('click', () => {
        state.path.push(node)
        render()
    })

    return card
}

function fileCard(file) {
    const card = document.createElement('div')
    card.className = 'formacion_card'
    card.dataset.fileId = String(file.id)

    const kind = document.createElement('div')
    kind.className = 'card_kind'
    kind.textContent = kindLabels()[file.kind] ?? file.kind
    card.appendChild(kind)

    card.appendChild(cardName(file.name))

    const meta = document.createElement('div')
    meta.className = 'card_meta'

    if (file.lang && file.lang !== 'na') {
        const lang = document.createElement('span')
        lang.className = 'badge_lang'
        lang.textContent = file.lang
        meta.appendChild(lang)
    }

    if (state.completed.has(file.id)) {
        const seen = document.createElement('span')
        seen.className = 'badge_seen'
        seen.textContent = '✓ ' + t('formacion_seen')
        meta.appendChild(seen)
    }

    card.appendChild(meta)
    card.addEventListener('click', () => openFile(file))

    return card
}

// ── Player con heartbeat (C5 / BIB-02) ─────────────────────────────────────

async function openFile(file) {
    const staff = getActiveStaff()
    if (!staff) return

    const isVideo = file.kind === 'pill_video' || file.kind === 'long_video'
    const isPdf = file.kind === 'pdf'

    if (!isVideo && !isPdf) {
        const response = file.kind === 'link'
            ? await getTrainingPlayUrl(file.id, staff.id)
            : await getTrainingDownloadUrl(file.id, staff.id)
        const url = response?.data?.url
        if (url) window.open(url, '_blank', 'noopener')
        return
    }

    const response = await getTrainingPlayUrl(file.id, staff.id)
    const url = response?.data?.url
    if (!url) return

    if (isPdf) {
        await openPdf(file, staff, url)
        return
    }

    openVideo(file, staff, url)
}

function showPlayer(file, documentMode) {
    el('formacion_player_title').textContent = file.name
    const player = el('formacion_player')
    player.classList.toggle('is-document', documentMode)
    player.classList.remove('hide')
    player.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

async function openPdf(file, staff, url) {
    clearMedia()
    state.player.file = file
    state.player.ended = true
    showPlayer(file, true)

    const video = el('formacion_video')
    video?.classList.add('hide')

    const iframe = el('formacion_pdf')
    iframe.classList.remove('hide')
    iframe.title = file.name
    iframe.src = url

    const tabLink = el('formacion_player_open_tab')
    if (tabLink) {
        tabLink.textContent = t('formacion_open_tab')
        tabLink.href = url
        tabLink.classList.remove('hide')
    }

    const payload = {
        file_id: file.id,
        position: 0,
        watched_delta: 0,
        ended: true,
    }
    if (staff.id !== null) payload.staff_id = staff.id

    const progress = await postTrainingProgress(payload)
    if (progress?.data?.completed) {
        markFileSeenInGrid(file.id)
    }
}

function openVideo(file, staff, url) {
    clearMedia()
    el('formacion_video')?.classList.remove('hide')

    const video = el('formacion_video')
    state.player.file = file
    state.player.ended = false

    showPlayer(file, false)
    video.src = url
    video.play().catch(() => undefined)

    video.onplay = startHeartbeat
    video.onpause = () => {
        stopHeartbeat()
        sendHeartbeat() // el tramo visto hasta la pausa
    }
    video.onended = async () => {
        state.player.ended = true
        stopHeartbeat()
        await sendHeartbeat(true)
        markFileSeenInGrid(file.id)
    }
    // URL expirada a mitad de vídeo: re-firmar y continuar donde iba (C5).
    video.onerror = async () => {
        if (!state.player.file) return
        const position = video.currentTime
        const retry = await getTrainingPlayUrl(state.player.file.id, staff.id)
        if (retry?.data?.url) {
            video.src = retry.data.url
            video.currentTime = position
            video.play().catch(() => undefined)
        }
    }
}

function markFileSeenInGrid(fileId) {
    state.completed.add(fileId)
    const card = el('formacion_grid')?.querySelector(`[data-file-id="${fileId}"]`)
    if (!card || card.querySelector('.badge_seen')) return
    const meta = card.querySelector('.card_meta')
    if (!meta) return
    const seen = document.createElement('span')
    seen.className = 'badge_seen'
    seen.textContent = '✓ ' + t('formacion_seen')
    meta.appendChild(seen)
}

function startHeartbeat() {
    stopHeartbeat()
    state.player.lastTick = Date.now()
    state.player.timer = setInterval(sendHeartbeat, HEARTBEAT_SECONDS * 1000)
}

function stopHeartbeat() {
    if (state.player.timer) {
        clearInterval(state.player.timer)
        state.player.timer = null
    }
}

async function sendHeartbeat(ended = false) {
    const staff = getActiveStaff()
    const file = state.player.file
    const video = el('formacion_video')
    if (!staff || !file || !video || file.kind === 'pdf') return

    const now = Date.now()
    const delta = Math.round((now - state.player.lastTick) / 1000)
    state.player.lastTick = now

    const payload = {
        file_id: file.id,
        position: Math.floor(video.currentTime || 0),
        watched_delta: Math.max(0, delta),
        ended: ended || state.player.ended,
    }
    if (staff.id !== null) payload.staff_id = staff.id

    const response = await postTrainingProgress(payload)

    if (response?.data?.completed) {
        markFileSeenInGrid(file.id)
    }
}

function closeQuizIfOpen() {
    const quizBox = el('formacion_quiz')
    if (quizBox && !quizBox.classList.contains('hide')) {
        quizBox.classList.add('hide')
        el('formacion_grid')?.classList.remove('hide')
        el('formacion_breadcrumb')?.classList.remove('hide')
    }
}

function clearMedia() {
    stopHeartbeat()
    const video = el('formacion_video')
    if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
        video.onplay = null
        video.onpause = null
        video.onended = null
        video.onerror = null
        video.classList.remove('hide')
    }
    const iframe = el('formacion_pdf')
    if (iframe) {
        iframe.src = 'about:blank'
        iframe.classList.add('hide')
    }
    const tabLink = el('formacion_player_open_tab')
    if (tabLink) {
        tabLink.removeAttribute('href')
        tabLink.classList.add('hide')
    }
    el('formacion_player')?.classList.remove('is-document')
}

function closePlayer() {
    clearMedia()
    state.player.file = null
    el('formacion_player')?.classList.add('hide')
}
